import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import BudgetsAllBudgets from 'src/app/BudgetsAllBudgets/BudgetsAllBudgets'
import BudgetsRequests from 'src/app/BudgetsRequests/BudgetsRequests'
import FamilyLayout, {
  FamilyLayoutTab,
} from 'src/component-layout/FamilyLayout/FamilyLayout'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useTranslate } from 'src/localization/Trns'
import { budgetsRouteTree } from 'src/navigation/budgetsRouteTree'
import { Rdrct } from 'src/navigation/Lnk'
import {
  actionUnavailableUrl,
  budgetsAllBudgetsUrl,
  budgetsNeedsApprovalUrl,
  budgetsRequestsUrl,
  budgetsUrl,
  createBudgetUrl,
  requestBudgetUrl,
} from 'src/navigation/urls'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  selectCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import {
  selectBudgetsAllBudgetsMatch,
  selectBudgetsNeedsApprovalMatch,
  selectBudgetsRequestsMatch,
  selectCalculatedBackgroundLocation,
} from 'src/redux/selectors/router.selectors'

// Stable test ids per family tab (preserve legacy selectors).
const FAMILY_TAB_TEST_IDS: Record<string, string> = {
  all: 'AllBudgetsBudgets',
  needsapproval: 'NeedsApprovalBudgets',
}

/**
 * BudgetsFamilyPage — route-level page for the Budgets family tabs (EX-39748).
 *
 * Owns family routing + data: dispatches the budget statistics, resolves the
 * active tab from the router, and renders the persistent FamilyLayout tab bar
 * with a nested <Switch> that swaps tab content beneath it. Tab content
 * components stay routing-free; this page is the only routing boundary.
 *
 * Gate: registered once for the whole family (see flatArchitectureRoutes.tsx),
 * so this page + its FamilyLayout Subnav stay mounted across tabs and the
 * sliding active bar animates.
 */
const BudgetsFamilyPage: React.FC = () => {
  const translate = useTranslate()
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const budgetsAllBudgetsMatch = useSelector(selectBudgetsAllBudgetsMatch)
  const budgetsRequestsMatch = useSelector(selectBudgetsRequestsMatch)
  const budgetsNeedsApprovalMatch = useSelector(selectBudgetsNeedsApprovalMatch)
  const creditCardsSourceBudgetsEnabledCanIssueBudgetsCount = useSelector(
    selectCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  )
  const creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount = useSelector(
    selectCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  )

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsSourceBudgetsEnabledCountStatistics,
      appStatisticsKeys.CreditCardsDelegateCountStatistics,
      appStatisticsKeys.CreditCardsSourceCountStatistics,
    ])
  }, [dispatchStatistics])

  const canIssueBudget = creditCardsSourceBudgetsEnabledCanIssueBudgetsCount > 0
  const canCurrentlyIssueBudget =
    creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount > 0

  const tabs: FamilyLayoutTab[] = useMemo(
    () =>
      budgetsRouteTree.family.tabs.map((tab) => ({
        key: tab.key,
        label: translate(tab.labelSource),
        to: tab.path,
        ariaLabelSource: tab.labelSource,
        testId: FAMILY_TAB_TEST_IDS[tab.key],
      })),
    [],
  )

  const activeTabKey =
    budgetsNeedsApprovalMatch !== null || budgetsRequestsMatch !== null
      ? 'needsapproval'
      : budgetsAllBudgetsMatch !== null
        ? 'all'
        : undefined

  const buttons = useMemo(
    () =>
      canIssueBudget
        ? [
            {
              design: ButtonDesigns.designCtaInverse,
              type: ButtonTypes.button,
              text: translate('Request Budget'),
              linkProps: {
                to: requestBudgetUrl(),
                ariaLabel: 'Request Budget',
              },
            },
            {
              design: ButtonDesigns.designCta,
              type: ButtonTypes.button,
              text: translate('Create Budget'),
              linkProps: {
                to: canCurrentlyIssueBudget
                  ? createBudgetUrl()
                  : actionUnavailableUrl(),
                ariaLabel: 'Create Budget',
              },
            },
          ]
        : [
            {
              design: ButtonDesigns.designCta,
              type: ButtonTypes.button,
              text: translate('Request Budget'),
              linkProps: {
                to: requestBudgetUrl(),
                ariaLabel: 'Request Budget',
              },
            },
          ],
    [canIssueBudget, canCurrentlyIssueBudget],
  )

  return !(
    statisticsLoaded.creditCardsSourceBudgetsEnabledCountStatisticsLoaded &&
    statisticsLoaded.creditCardsDelegateCountStatisticsLoaded &&
    statisticsLoaded.creditCardsSourceCountStatisticsLoaded
  ) ? (
    <LoadingBackgroundPage />
  ) : (
    <LayoutShell
      subnavOptions={[]}
      subnavDesign={SubnavDesign.Floating}
      title={translate('Budgets')}
      buttons={buttons}
      content={
        <FamilyLayout tabs={tabs} activeTabKey={activeTabKey}>
          <Switch location={calculatedBackgroundLocation}>
            <Route exact path={budgetsAllBudgetsUrl()}>
              <BudgetsAllBudgets />
            </Route>
            <Route exact path={budgetsNeedsApprovalUrl()}>
              <BudgetsRequests />
            </Route>
            <Route exact path={budgetsRequestsUrl()}>
              <BudgetsRequests />
            </Route>
            <Route exact path={budgetsUrl()}>
              <Rdrct to={budgetsRouteTree.family.redirectTo} />
            </Route>
            <Route path={'/'}>
              <NotFoundBackgroundPageContent />
            </Route>
          </Switch>
        </FamilyLayout>
      }
    />
  )
}

export default BudgetsFamilyPage
